import { useEffect, useState, useCallback } from 'react'
import { calendarFeedApi, CalendarFeedPreviewItem, CalendarFeedPreviewSettings } from '@/services/api/calendarFeed'
import { Eye } from 'lucide-react'

const TYPE_LABELS: Record<string, { label: string; badge: string }> = {
  open_request: { label: 'Offene Anfrage', badge: 'bg-purple-50 text-purple-700' },
  absence: { label: 'Abwesend', badge: 'bg-amber-50 text-amber-700' },
}

const SUBSTITUTION_LABEL = { label: 'Vertretung', badge: 'bg-emerald-50 text-emerald-700' }
const CANCELLED_LABEL = { label: 'Storniert', badge: 'bg-red-50 text-red-700' }

function entryLabel(entry: CalendarFeedPreviewItem): { label: string; badge: string } {
  if (entry.cancelled) return CANCELLED_LABEL
  if (entry.entry_type === 'open_request') return TYPE_LABELS.open_request ?? SUBSTITUTION_LABEL
  if (entry.role === 'requester') return TYPE_LABELS.absence ?? SUBSTITUTION_LABEL
  return SUBSTITUTION_LABEL
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return 'Datum unbekannt'
  const d = new Date(`${dateStr}T00:00:00`)
  if (Number.isNaN(d.getTime())) return dateStr
  return d.toLocaleDateString('de-CH', { weekday: 'short', day: '2-digit', month: '2-digit' })
}

export function CalendarFeedPreview({ settings }: { settings: CalendarFeedPreviewSettings }) {
  const [entries, setEntries] = useState<CalendarFeedPreviewItem[] | null>(null)
  const [status, setStatus] = useState<string>('ok')
  // feed-v3 V2: Server-Echo der Settings (z.B. nach Fenster-Kappe) —
  // Header/Meldungen zeigen das effektive Fenster, nicht die Formular-Werte.
  const [echoDaysAhead, setEchoDaysAhead] = useState<number | null>(null)
  const [echoDaysPast, setEchoDaysPast] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  // Destrukturierte Primitive statt settings-Objekt in den Deps — das
  // Parent erstellt das Objekt bei jedem Render neu, sonst refetched die
  // Vorschau endlos.
  const {
    feed_include_substitutions: incSub,
    feed_include_absences: incAbs,
    feed_include_cancelled: incCan,
    feed_include_open_requests: incOpen,
    feed_show_subject: showSubject,
    feed_show_klass: showKlass,
    feed_show_room: showRoom,
    feed_show_teacher: showTeacher,
  } = settings

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await calendarFeedApi.getPreview({
        feed_include_substitutions: incSub,
        feed_include_absences: incAbs,
        feed_include_cancelled: incCan,
        feed_include_open_requests: incOpen,
        feed_show_subject: showSubject,
        feed_show_klass: showKlass,
        feed_show_room: showRoom,
        feed_show_teacher: showTeacher,
      }, 5)
      setEntries(data.entries)
      setStatus(data.status || 'ok')
      const echo = data.settings || {}
      setEchoDaysAhead(typeof echo.window_days_ahead === 'number' ? echo.window_days_ahead : null)
      setEchoDaysPast(typeof echo.window_days_past === 'number' ? echo.window_days_past : null)
    } catch (e: unknown) {
      const err = e as { detail?: string; message?: string }
      setError(err?.detail || err?.message || 'Vorschau konnte nicht geladen werden')
    } finally {
      setLoading(false)
    }
  }, [incSub, incAbs, incCan, incOpen, showSubject, showKlass, showRoom, showTeacher])

  useEffect(() => {
    load()
  }, [load])

  const displayDaysAhead = echoDaysAhead ?? 30
  const displayDaysPast = echoDaysPast ?? 7

  const anyCategoryOn = incSub || incAbs || incOpen

  const emptyMessage =
    status === 'no_subscription'
      ? 'Noch kein Kalender-Abo — verbinde deinen Kalender auf dem Dashboard («Kalender verbinden»), um Ereignisse zu sehen.'
      : status === 'export_disabled'
        ? 'Der Kalender-Export ist für deine Schule deaktiviert.'
        : !anyCategoryOn
          ? 'Keine Einträge — aktiviere mindestens eine Kategorie oben.'
          : `Keine kommenden Einträge in den nächsten ${displayDaysAhead} Tagen.`

  const showEmpty = !loading && !error && (entries === null || entries.length === 0)

  return (
    <div className="mt-6 pt-6 border-t border-gray-200">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Eye className="w-4 h-4 text-indigo-600" />
          <h4 className="text-sm font-semibold text-gray-900">Vorschau — so sieht dein Feed aus</h4>
        </div>
        <span className="text-xs text-gray-400" data-testid="preview-range">
          {displayDaysPast} Tage zurück – {displayDaysAhead} Tage voraus
        </span>
      </div>

      {loading && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-2">
          {[1, 2, 3].map(i => (
            <div key={i} className="flex items-center gap-3 p-2 rounded-lg bg-white border border-gray-100 animate-pulse">
              <div className="w-9 h-9 rounded-lg bg-gray-200" />
              <div className="flex-1 space-y-1">
                <div className="h-3 bg-gray-200 rounded w-2/3" />
                <div className="h-2 bg-gray-100 rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">{error}</div>
      )}

      {showEmpty && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm text-gray-500 text-center">
          {emptyMessage}
        </div>
      )}

      {!loading && !error && entries && entries.length > 0 && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-2">
          {entries.map((entry, i) => {
            const meta = entryLabel(entry)
            const titleParts = []
            if (showSubject && entry.subject_name) titleParts.push(entry.subject_name)
            if (showKlass && entry.klass_name) titleParts.push(entry.klass_name)
            const title = titleParts.join(' ') || 'Vertretung'
            const detailLine =
              entry.entry_type === 'open_request'
                ? `Von ${entry.requester_name || '?'}`
                : entry.role === 'requester'
                  ? `Vertretung durch ${entry.substitute_name || '?'}`
                  : `Bei ${entry.requester_name || '?'}`
            return (
              <div key={i} className="flex items-center gap-3 p-2 rounded-lg bg-white border border-gray-100">
                <div className="w-9 h-9 rounded-lg bg-indigo-600 text-white flex items-center justify-center text-[11px] font-semibold flex-shrink-0">
                  {formatDate(entry.date_str).split(' ')[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`badge ${meta.badge}`}>{meta.label}</span>
                    <span className={`text-sm font-medium text-gray-900 truncate ${entry.cancelled ? 'line-through' : ''}`}>
                      {title}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 truncate">
                    {formatDate(entry.date_str)} · {entry.start_time || '?'}–{entry.end_time || '?'}
                    {showRoom && entry.room_name ? ` · ${entry.room_name}` : ''}
                    {showTeacher ? ` · ${detailLine}` : ''}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      <p className="text-xs text-gray-400 mt-2">
        Live aktualisiert mit deinen Einstellungen. Der abonnierte Kalender (Google, Apple, Outlook) aktualisiert sich bei der nächsten Synchronisation.
      </p>
    </div>
  )
}
